import isEmpty from "lodash/isEmpty";
import {
  BookingDataItemLimitation,
  BookingFormMode
} from "PFApp/booking/components/booking_form/booking_form.types";
import { BookingType } from "PFApp/booking/components/booking_form/booking_type";
import { usePotentialWarningsQuery } from "PFApp/booking/components/booking_form/use_potential_warnings/use_potential_warnings_query";
import { useBookingsFromAvailability } from "PFCore/hooks/queries/bookings/bookings_from_availability/use_bookings_from_availability";
import useBookingCategories from "PFCore/hooks/use_booking_categories";

import { mapBookingDataToRequirement } from "../../../../booking/components/booking_form/requirement_selector";

const SOFT_CATEGORY_KEY = "soft";

type UseIsPotentiallyOverbooked = {
  profileId: number;
  activityId: number;
};
type UseIsPotentiallyOverbookedReturn = {
  isOverbooked: boolean;
  isFetching: boolean;
};

export const useIsPotentiallyOverbooked = ({
  profileId,
  activityId
}: UseIsPotentiallyOverbooked): UseIsPotentiallyOverbookedReturn => {
  const { bookingCategories } = useBookingCategories();
  const softCategory = bookingCategories.find(({ key }) => key === SOFT_CATEGORY_KEY);

  const {
    data: bookingsFromAvailabilityData,
    isFetched: isBookingsFromAvailabilityFetched,
    isFetching: isBookingsFromAvailabilityFetching
  } = useBookingsFromAvailability({
    activityId,
    profileId
  });

  const { data, isFetching: isPotentialWarningsFetching } = usePotentialWarningsQuery(
    {
      values: {
        activityId,
        bookingType: BookingType.Single,
        bookings: (bookingsFromAvailabilityData?.ranges ?? []).map(
          ({
            start_date: startDate,
            end_date: endDate,
            requirement_type: requirementType,
            requirement_value: requirementValue
          }) => ({
            startDate,
            endDate,
            bookingCategoryId: softCategory?.id,
            overridesCalendar: true,
            overridesNonWorkingDays: false,
            requirement: mapBookingDataToRequirement({ requirementType, requirementValue }),
            limitation: BookingDataItemLimitation.None
          })
        ),
        globalCategory: softCategory,
        globalOverridesDiaryTime: true,
        globalOverridesNonWorkingTime: false,
        workforceMember: {
          id: profileId
        }
      },
      mode: BookingFormMode.Create,
      initialBookings: []
    },
    {
      enabled: isBookingsFromAvailabilityFetched && !isEmpty(bookingsFromAvailabilityData?.ranges),
      retry: 0,
      gcTime: 0
    }
  );

  const isOverbooked = !isEmpty(data?.overbookings);
  const isFetching = isBookingsFromAvailabilityFetching || isPotentialWarningsFetching;

  return { isOverbooked, isFetching };
};
